import interpolateComponents from '@automattic/interpolate-components';
import { useState } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiOrg } from 'api/org/types/ApiOrg';
import { Box } from 'components/Box';
import { CloseButton } from 'components/Button';
import { Grid, GridItem } from 'components/Grid';
import { TrackedLink } from 'components/Link';
import { BodySmall } from 'components/Text/BodySmall';
import { colors } from 'theme/theme';
import { shorterDate } from 'utils/date';
import { getItem, setItem } from 'utils/localStorage';
import { getRgbaString } from 'utils/style';

const contactLink = (
  <TrackedLink
    href="https://www.idealist.org/en/about/contact"
    target="_blank"
    eventName="Org Preview - Contact Idealist"
    color="white"
  />
);

type Props = {
  org: ApiOrg;
};

export function OrgPreviewInfo({ org }: Props) {
  const createdDate = shorterDate(org.created);
  const submittedToday = createdDate === shorterDate(Date.now());
  const previouslyDismissed = getItem('orgPreviewInfoDismissed') || {};
  const [dismissed, setDismissed] = useState(previouslyDismissed[org.id]);

  const closeInfo = () => {
    const dismissedList = localStorage.getItem('orgPreviewInfoDismissed') || {};
    setItem('orgPreviewInfoDismissed', { ...dismissedList, [org.id]: true });
    setDismissed(true);
  };

  return dismissed ? null : (
    <Box
      bg={`rgba(${getRgbaString(colors.brandBlue, 0.1)})`}
      py={40}
      position="relative"
    >
      <Box position="absolute" top={12} right="4px">
        <CloseButton onClick={closeInfo} />
      </Box>
      <Grid>
        <GridItem columnStart={[1, 1, 1, 1]} columnEnd={[13, 13, 13, 13]}>
          <Box pr={20}>
            <BodySmall>
              {submittedToday ? (
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {org.is501c3
                    ? interpolateComponents({
                        mixedString: getText(
                          'Thank you! Your organization has been submitted for approval and will be reviewed within two business days. In the meantime, you can customize your profile, create a job listing, or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions.',
                        ),
                        components: {
                          link: contactLink,
                        },
                      })
                    : interpolateComponents({
                        mixedString: getText(
                          'Thank you! Your organization has been submitted for approval and will be reviewed within two business days. In the meantime, you can customize your profile or contact the {{link}}Idealist Community Support Team{{/link}} if you have any questions.',
                        ),
                        components: {
                          link: contactLink,
                        },
                      })}
                </>
              ) : (
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {org.is501c3
                    ? interpolateComponents({
                        mixedString: sprintf(
                          getText(
                            'Your organization was submitted for approval on %(createdDate)s. In the meantime, you can customize your profile or create a job listing. If you have any questions or if it has been more than two business days since applying, please contact the {{link}}Idealist Community Support Team{{/link}}.',
                          ),
                          {
                            createdDate,
                          },
                        ),
                        components: {
                          link: contactLink,
                          // TODO: fix types
                          // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'Element'.
                          createdDate,
                        },
                      })
                    : interpolateComponents({
                        mixedString: sprintf(
                          getText(
                            'Your organization was submitted for approval on %(createdDate)s. In the meantime, you can customize your profile. If you have any questions or if it has been more than two business days since applying, please contact the {{link}}Idealist Community Support Team{{/link}}.',
                          ),
                          {
                            createdDate,
                          },
                        ),
                        components: {
                          link: contactLink,
                          // TODO: fix types
                          // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'Element'.
                          createdDate,
                        },
                      })}
                </>
              )}
            </BodySmall>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
}
